/**
 * Post-Authentication Redirect
 *
 * Handles redirects after successful authentication
 */

import { getSafeRedirectUrl, clearReturnUrl } from "./returnUrlHandlers.js";
import { getAuthDataForRedirect } from "./authDataHelpers.js";

/**
 * Perform post-authentication redirect
 * @param {URLSearchParams} searchParams - URL search parameters
 * @param {Object} locationState - React Router location state
 * @param {Function} navigate - React Router navigate function (optional)
 * @returns {string} - The redirect URL that will be used
 */
export const performPostAuthRedirect = (
  searchParams = null,
  locationState = null,
  navigate = null,
) => {
  const redirectUrl = getSafeRedirectUrl(searchParams, locationState, true);

  // Clean up any stored return URL
  clearReturnUrl();

  // Check if it's a same-origin URL and we have React Router navigate
  try {
    const url = new URL(redirectUrl);
    const isSameOrigin = url.origin === window.location.origin;

    if (isSameOrigin && navigate) {
      // Use React Router for same-origin navigation
      const path = url.pathname + url.search + url.hash;
      navigate(path, { replace: true });
    } else {
      // For cross-origin redirects, append auth data as URL parameters
      // This is a workaround for IP-based deployments where cookies don't work across ports

      const { token, userData } = getAuthDataForRedirect();

      if (token && userData) {
        url.searchParams.set("token", token);
        url.searchParams.set("user", encodeURIComponent(userData));
      }

      const finalUrl = url.toString();
      window.location.href = finalUrl;
    }
  } catch (error) {
    console.error("Error during redirect:", error);
    // Fallback to window.location
    window.location.href = redirectUrl;
  }

  return redirectUrl;
};
